import { AGENCY_TIME_ZONE } from '../time/agency-dates.js';
import { offerOutForSignature } from './offers.js';
/**
 * What must be true before an offer goes out, written once (#262, frame 06b `297:932`).
 *
 * The frame draws three ticks under "Before you send" and its label column says "Checked again
 * when you press Send offer." That sentence is only honest if the screen and the server are
 * reading the SAME rule, so this module is the rule and both call it: `hr-workflow.ts` turns a
 * failure into its refusal, and the Send offer screen renders the same three statements.
 *
 * DERIVED, NEVER STORED (CLAUDE.md rule 4). Nothing here is a new field or a second definition
 * of "may this be sent" — each check reads records the person page already has.
 *
 * The refusal strings are the ones the API already answered with before this module existed.
 * They are moved here, not rewritten: HR has seen this wording, and a second phrasing of the
 * same refusal is how two screens start disagreeing about why something was blocked.
 */
export const offerSendCheckIds = ['deadline-ahead', 'no-other-offer-out', 'email-on-file', 'rates-set'];
/**
 * The acceptance deadline as a person reads it: `Sep 21, 2026, 5:00 PM`.
 *
 * With the year, which the Offers list's `dayAndTime` leaves off — that list is a scan of what
 * is in flight now, while this date is quoted into an email the candidate keeps and into the
 * sentence they are asked to act on. Always the agency's zone, never the reader's: HR in one
 * timezone and a candidate in another must be told the same hour.
 */
export function offerAcceptByText(instant, locale = 'en-US') {
    return new Intl.DateTimeFormat(locale, {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: AGENCY_TIME_ZONE,
    }).format(new Date(instant));
}
/**
 * The three checks, in the frame's order, each with whether it passes.
 *
 * Every check is evaluated — the caller gets all three, not the first failure — because the
 * screen shows all three at once and HR should see everything that needs fixing, not be walked
 * through them one refusal at a time.
 */
export function offerSendChecks(input) {
    const { offer, records, steps, firstName, hasEmailOnFile, now } = input;
    /**
     * Any OTHER offer still out for signature: issued and not yet accepted.
     *
     * Not merely `status === 'issued'`. A portal signature never changes the record's status
     * (`offerAcceptedAt` reads the approved step instead), so a signed offer still reads as issued;
     * counting it would stop anyone who has signed an offer from being sent a second (a raise, a
     * change of role). `offerOutForSignature` is the one definition that tells the two apart. An
     * unsigned issued offer, expired or not, still blocks, as it always has: withdraw it first.
     */
    const otherIssued = records.some((record) => record.id !== offer.id && offerOutForSignature(record, steps));
    /**
     * Every rate is above $0, and there is at least one. A rate defaults to 0 when a row is added or
     * its unit changes (#348), so a $0 is easy to save and the editor does not refuse it; this is where
     * it is stopped, at Send only (Julio, 2026-10-03). MILEAGE IS EXEMPT: a $0 mileage row can be a
     * deliberate "no reimbursement". An offer whose only rows are mileage has no rate at all, so it fails.
     */
    const rates = offer.terms.compensation.filter((row) => row.unit !== 'mile');
    const ratesSet = rates.length > 0 && rates.every((row) => row.amount > 0);
    return [
        {
            id: 'deadline-ahead',
            // Milliseconds, not calendar days: an offer that expires at 5:00 PM today is still live at
            // 4:59 and dead at 5:01, and this is the comparison `issue-offer` has always made.
            ok: Date.parse(offer.terms.acceptanceDeadline) > Date.parse(now),
            label: `Accept by is still ahead: ${offerAcceptByText(offer.terms.acceptanceDeadline)}`,
            refusal: 'This offer deadline has passed. Create a new draft.',
        },
        {
            id: 'no-other-offer-out',
            ok: !otherIssued,
            label: `No other offer is out for signature for ${firstName}`,
            refusal: 'An issued offer already exists. Preserve signed offers; withdraw an unsigned offer before issuing changed terms.',
        },
        {
            id: 'email-on-file',
            ok: hasEmailOnFile,
            label: `${firstName} has an email address on file`,
            refusal: 'This person has no email address on file.',
        },
        {
            id: 'rates-set',
            ok: ratesSet,
            label: 'Every rate is more than $0',
            refusal: 'A rate is $0, or the offer has no rate. Enter each rate before sending.',
        },
    ];
}
/**
 * The one refusal to answer with, or null when the offer may go out.
 *
 * First failure in the frame's order, so the reason HR is given is the first one they read on
 * the screen rather than whichever check happened to be written last.
 */
export function offerSendRefusal(input) {
    return offerSendChecks(input).find((check) => !check.ok)?.refusal ?? null;
}
//# sourceMappingURL=offer-send.js.map